// frontend/src/app/claim-annotator/domain/annotation-store.ts
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { AnnotationRow, AnnotationType, NewAnnotation, PdfDocumentRow } from './types';
import { ANNOTATION_REPOSITORY } from '../ports/annotation-repository.port';
import { ANNOTATION_DRAFT_CACHE } from '../ports/annotation-draft-cache.port';
import { colorForClaim } from './color-palette';

function uuid(): string {
  return crypto.randomUUID();
}
function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Feature-scoped store. Provided by the page component. Not
 * `providedIn: 'root'` because its lifetime is the page's, not the app's,
 * and because the repository it depends on is also page-scoped.
 */
@Injectable()
export class AnnotationStore {
  private readonly _documents = signal<PdfDocumentRow[]>([]);
  private readonly _annotations = signal<AnnotationRow[]>([]);
  private readonly _activeDocument = signal<string | null>(null);
  private readonly _selectedAnnotation = signal<string | null>(null);
  /**
   * Per-document last-submitted snapshot, used to compute `hasUnsubmittedChanges`.
   * Populated on `hydrateFromRepository` and on successful `submitChanges`.
   *
   * INVARIANT: every mutation method below MUST return new annotation row objects
   * (e.g. via `{ ...a, note }` spread) rather than mutating in place. The
   * `shallowEqualById` diff relies on reference identity to detect edits.
   */
  private readonly _submittedSnapshot = signal<Map<string, readonly AnnotationRow[]>>(new Map());

  /** Set of document ids that were restored from the crash cache during hydration. */
  private readonly _recoveredDocuments = signal<Set<string>>(new Set());

  readonly documents: Signal<readonly PdfDocumentRow[]> = this._documents.asReadonly();
  readonly annotations: Signal<readonly AnnotationRow[]> = this._annotations.asReadonly();
  readonly activeDocumentId: Signal<string | null> = this._activeDocument.asReadonly();
  readonly selectedAnnotationId: Signal<string | null> = this._selectedAnnotation.asReadonly();

  private readonly _activeSessionId = uuid();
  readonly activeSessionId: Signal<string> = computed(() => this._activeSessionId);

  readonly hasUnsubmittedChanges: Signal<boolean> = computed(() => {
    const docId = this._activeDocument();
    if (!docId) return false;
    const current = this._annotations().filter((a) => a.document_id === docId);
    const snapshot = this._submittedSnapshot().get(docId) ?? [];
    return !shallowEqualById(current, snapshot);
  });

  /** True if ANY tracked document has unsaved changes relative to its submitted snapshot. */
  readonly hasAnyUnsavedChanges: Signal<boolean> = computed(() => {
    const all = this._annotations();
    const snaps = this._submittedSnapshot();
    const docIds = new Set(all.map((a) => a.document_id));
    for (const docId of docIds) {
      const current = all.filter((a) => a.document_id === docId);
      const snapshot = snaps.get(docId) ?? [];
      if (!shallowEqualById(current, snapshot)) return true;
    }
    // Also catch documents whose rows were all removed locally (non-empty submitted snapshot,
    // now-empty current set), whose ids are therefore absent from the loop above.
    for (const [docId, snapshot] of snaps) {
      if (snapshot.length > 0) {
        const current = all.filter((a) => a.document_id === docId);
        if (!shallowEqualById(current, snapshot)) return true;
      }
    }
    return false;
  });

  /** Signal exposing the set of document ids that were recovered from the crash cache. */
  readonly recoveredDocuments: Signal<ReadonlySet<string>> = this._recoveredDocuments.asReadonly();

  private repo = inject(ANNOTATION_REPOSITORY);
  private cache = inject(ANNOTATION_DRAFT_CACHE);
  private _submitInFlight: Promise<void> | null = null;

  /** Write the current in-memory rows for a document into the crash cache. */
  private persistDraft(documentId: string): void {
    const rows = this._annotations().filter((a) => a.document_id === documentId);
    this.cache.write(documentId, rows);
  }

  openDocument(meta: Omit<PdfDocumentRow, 'id' | 'created_at'>): string {
    const existing = this._documents().find((d) => d.content_hash === meta.content_hash);
    if (existing) {
      this._activeDocument.set(existing.id);
      return existing.id;
    }
    // Use the content_hash as the document id so localStorage keys are stable
    // across page refreshes -- opening the same PDF restores its claims and annotations.
    const row: PdfDocumentRow = { id: meta.content_hash, created_at: nowIso(), ...meta };
    this._documents.update((arr) => [...arr, row]);
    this._activeDocument.set(row.id);
    return row.id;
  }

  setActiveDocument(id: string | null): void {
    this._activeDocument.set(id);
  }

  async hydrateFromRepository(documentId: string): Promise<AnnotationRow[]> {
    const loaded = await this.repo.loadFor(documentId);
    // Establish the backend baseline in memory and as the submitted snapshot.
    this._annotations.update((arr) => [
      ...arr.filter((a) => a.document_id !== documentId),
      ...loaded,
    ]);
    this._submittedSnapshot.update((m) => {
      const next = new Map(m);
      next.set(documentId, loaded);
      return next;
    });

    // Check the crash cache: adopt it only when structurally different from the baseline.
    const cached = this.cache.read(documentId);
    if (cached !== null && !structurallyEqual(cached, loaded)) {
      // Restore cached rows (working state becomes dirty vs the snapshot).
      this._annotations.update((arr) => [
        ...arr.filter((a) => a.document_id !== documentId),
        ...cached,
      ]);
      // Mark as recovered so the page can show a recovery toast.
      this._recoveredDocuments.update((s) => new Set([...s, documentId]));
    } else {
      // Cache is absent or identical to backend: drop stale-equal cache entries.
      this.cache.clear(documentId);
    }

    return loaded;
  }

  /**
   * Push the current annotations for the document to the repository. On success,
   * the new local state becomes the baseline for `hasUnsubmittedChanges`.
   * Throws if the repository's `submitAll` rejects.
   *
   * Single-flight: concurrent calls share the same in-flight promise so the
   * repository never sees overlapping submissions for the same store.
   */
  submitChanges(documentId: string): Promise<void> {
    if (this._submitInFlight) return this._submitInFlight;
    this._submitInFlight = (async () => {
      try {
        const current = this._annotations().filter((a) => a.document_id === documentId);
        await this.repo.submitAll(documentId, current);
        this._submittedSnapshot.update((m) => {
          const next = new Map(m);
          next.set(documentId, current);
          return next;
        });
        // Only clear cache on success; keep it on failure so re-login can retry.
        this.cache.clear(documentId);
      } finally {
        this._submitInFlight = null;
      }
    })();
    return this._submitInFlight;
  }

  /**
   * Throw away local changes for a document and restore the last-submitted
   * snapshot. No repository call.
   */
  discardChanges(documentId: string): void {
    const snapshot = this._submittedSnapshot().get(documentId) ?? [];
    this._annotations.update((arr) => [
      ...arr.filter((a) => a.document_id !== documentId),
      ...snapshot,
    ]);
    if (this._selectedAnnotation() !== null) {
      const stillExists = snapshot.some((a) => a.id === this._selectedAnnotation());
      if (!stillExists) this._selectedAnnotation.set(null);
    }
    this.cache.clear(documentId);
  }

  addAnnotation(input: Omit<NewAnnotation, 'type'> & { type?: AnnotationType }): AnnotationRow {
    const row: AnnotationRow = {
      id: uuid(),
      created_at: nowIso(),
      type: input.type ?? 'box',
      ...input,
    } as AnnotationRow;
    this._annotations.update((arr) => [...arr, row]);
    this.persistDraft(input.document_id as string);
    return row;
  }

  setNote(id: string, note: string | undefined): void {
    this._annotations.update((arr) => arr.map((a) => (a.id === id ? { ...a, note } : a)));
    const docId = this._annotations().find((a) => a.id === id)?.document_id;
    if (docId) this.persistDraft(docId);
  }

  setText(id: string, text: string, size: { width: number; height: number }): void {
    this._annotations.update((arr) =>
      arr.map((a) => (a.id === id ? { ...a, text, width: size.width, height: size.height } : a)),
    );
    const docId = this._annotations().find((a) => a.id === id)?.document_id;
    if (docId) this.persistDraft(docId);
  }

  setOcrText(id: string, ocrText: string): void {
    this._annotations.update((arr) =>
      arr.map((a) => (a.id === id ? { ...a, ocr_text: ocrText } : a)),
    );
    const docId = this._annotations().find((a) => a.id === id)?.document_id;
    if (docId) this.persistDraft(docId);
  }

  async runOcr(annotation: AnnotationRow): Promise<void> {
    const docId = this._activeDocument();
    if (!docId) return;
    const updated = await this.repo.runOcr(docId, annotation);
    this._annotations.update((arr) =>
      arr.map((a) => (a.id === annotation.id ? { ...a, ocr_text: updated.ocr_text } : a)),
    );
    this.persistDraft(docId);
  }

  removeAnnotation(id: string): void {
    const docId = this._annotations().find((a) => a.id === id)?.document_id;
    this._annotations.update((arr) => arr.filter((a) => a.id !== id));
    if (this._selectedAnnotation() === id) this._selectedAnnotation.set(null);
    if (docId) this.persistDraft(docId);
  }

  selectAnnotation(id: string | null): void {
    this._selectedAnnotation.set(id);
  }

  annotationsByClaim(claimId: string): AnnotationRow[] {
    const docId = this._activeDocument();
    return this._annotations().filter((a) => a.claim_id === claimId && a.document_id === docId);
  }

  pagesWithBoxes(claimId: string): number[] {
    const docId = this._activeDocument();
    const set = new Set<number>();
    for (const a of this._annotations())
      if (a.claim_id === claimId && a.document_id === docId) set.add(a.page);
    return Array.from(set).sort((a, b) => a - b);
  }

  colorForClaim(claimId: string): string {
    return colorForClaim(claimId);
  }

  async deleteDocument(id: string): Promise<void> {
    this._documents.update((arr) => arr.filter((d) => d.id !== id));
    this._annotations.update((arr) => arr.filter((a) => a.document_id !== id));
    this._submittedSnapshot.update((m) => {
      const next = new Map(m);
      next.delete(id);
      return next;
    });
    if (this._activeDocument() === id) this._activeDocument.set(null);
    this.cache.clear(id);
    await this.repo.delete(id);
  }

  /** Remove the given document id from the recovered-documents set after the page has handled it. */
  acknowledgeRecovery(documentId: string): void {
    this._recoveredDocuments.update((s) => {
      const next = new Set(s);
      next.delete(documentId);
      return next;
    });
  }
}

function shallowEqualById(a: readonly AnnotationRow[], b: readonly AnnotationRow[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((r) => [r.id, r]));
  for (const x of a) {
    const other = byId.get(x.id);
    if (!other) return false;
    // Reference identity is intentional: mutation methods return new objects
    // (see INVARIANT on `_submittedSnapshot` above). A change to any field
    // produces a fresh row, so reference inequality means "edited".
    if (other !== x) return false;
  }
  return true;
}

/**
 * Deep structural equality for two AnnotationRow arrays: compares by value
 * of every field, not by reference. Used to decide whether a cached draft
 * genuinely diverges from the backend baseline.
 */
function structurallyEqual(a: readonly AnnotationRow[], b: readonly AnnotationRow[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((r) => [r.id, r]));
  for (const x of a) {
    const other = byId.get(x.id);
    if (!other) return false;
    if (!fieldsEqual(x, other)) return false;
  }
  return true;
}

function fieldsEqual(a: AnnotationRow, b: AnnotationRow): boolean {
  return (
    a.id === b.id &&
    a.document_id === b.document_id &&
    a.claim_id === b.claim_id &&
    a.type === b.type &&
    a.page === b.page &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    a.color === b.color &&
    (a.text ?? '') === (b.text ?? '') &&
    (a.note ?? '') === (b.note ?? '') &&
    (a.ocr_text ?? '') === (b.ocr_text ?? '') &&
    a.session_id === b.session_id &&
    a.created_at === b.created_at
  );
}
